import { useState } from "react";

import {
  Button,
  Colors,
  Icon,
  Menu,
  MenuItem,
  Position,
} from "@blueprintjs/core";
import some from "lodash/some";

import OutlookIcon from "@/../../hypetrain/static/img/outlook-icon.svg";
import { Popover } from "@/components/helpers/ui/blueprint-overrides/Popover";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { RoundedIconButton } from "@/components/pieces/form/RoundedIconButton";
import {
  GOOGLE_REQUIRED_SCOPES,
  GoogleIcon,
  GrantCalendarAccess,
} from "@/components/pieces/google_login/GrantCalendarAccess";
import {
  GrantOutlookCalendarAccess,
  OUTLOOK_REQUIRED_SCOPES,
} from "@/components/pieces/outlook-login/GrantOutlookCalendarAccess";
import { SUBTEXT_COLOR } from "@/css/constants";
import {
  CalendarAccountAccountType,
  MakePrimaryCalendarAccountMutationFn,
  RemoveCalendarAccountMutationFn,
} from "@/graphql";
import { CalendarAccount } from "@/types/calendars";
import { ensureNonNullArray } from "@/utils/array";

function validateAccountScopes(acc: CalendarAccount) {
  const required_scopes =
    acc.accountType === "GOOGLE"
      ? GOOGLE_REQUIRED_SCOPES
      : OUTLOOK_REQUIRED_SCOPES;

  return required_scopes.reduce((hasPermissionError, scope) => {
    if (!acc.scopes || !acc.scopes.includes(scope)) {
      return true;
    }
    return hasPermissionError;
  }, false);
}

function isAccountHasCalendarError(acc: CalendarAccount) {
  const calendars = acc?.calendars || [];
  return some(calendars, (calendar) => calendar && calendar.error);
}

interface CalendarAccountBlockProps {
  account: CalendarAccount;
  isPrimary: boolean;
  updating: boolean;
  makePrimary: MakePrimaryCalendarAccountMutationFn;
  removeAccount: RemoveCalendarAccountMutationFn;
  loading: boolean;
  responseGoogleHandler: ({ code }: { code: string }) => void;
  onOpenCalendarSettingModal: (account: CalendarAccount) => void;
  workspace?: string;
}

export function CalendarAccountBlock({
  account,
  isPrimary,
  updating,
  makePrimary,
  removeAccount,
  loading,
  responseGoogleHandler,
  onOpenCalendarSettingModal,
  workspace,
}: CalendarAccountBlockProps) {
  const hasPermissionError = validateAccountScopes(account);
  const hasAccountError = account?.error;
  const hasCalendarError = isAccountHasCalendarError(account);
  const hasError = hasPermissionError || hasAccountError || hasCalendarError;
  const [isOpen, setIsOpen] = useState(false);
  const calendars = ensureNonNullArray(account?.calendars);

  return (
    <Box
      backgroundColor={hasError ? "#ff807c1f" : undefined}
      className="round-corners bas"
      mb={3}
    >
      <Flex
        justifyContent="space-between"
        alignItems="flex-start"
        py={2}
        px={3}
      >
        <Flex
          alignItems="center"
          fontWeight={500}
          css={{ overflowWrap: "anywhere" }}
          mr={2}
        >
          <Box>
            <Flex alignItems="center" mr={3}>
              <Box minWidth={18} height={18} mr={3}>
                {account.accountType === CalendarAccountAccountType.Google ? (
                  <GoogleIcon size={18} />
                ) : (
                  <EmbedSvg
                    width={18}
                    height={18}
                    src={OutlookIcon}
                    style={{ position: "absolute" }}
                  />
                )}
              </Box>
              {account.accountEmail}
            </Flex>
            <Box color={SUBTEXT_COLOR} fontWeight={400} ml={34}>
              {calendars?.length > 0 ? (
                calendars.map((calendar) => (
                  <Flex key={calendar.id}>
                    <Box mr={2}>&bull;</Box>
                    <Box>{calendar.name}</Box>
                  </Flex>
                ))
              ) : (
                <Flex>
                  <Box mr={2}>&bull;</Box>
                  <Box>Default Calendar</Box>
                </Flex>
              )}
            </Box>
          </Box>
        </Flex>
        <Popover
          content={
            <Menu>
              {account.accountType === CalendarAccountAccountType.Google ? (
                <MenuItem
                  text="Calendar Settings"
                  icon="cog"
                  onClick={() => {
                    onOpenCalendarSettingModal(account);
                    setIsOpen(false);
                  }}
                />
              ) : null}
              {account.accountType === CalendarAccountAccountType.Google &&
                !isPrimary && (
                  <MenuItem
                    text="Make Primary"
                    icon="star"
                    onClick={() => {
                      makePrimary({ variables: { id: account.id } });
                      setIsOpen(false);
                    }}
                  />
                )}
              {!isPrimary && (
                <MenuItem
                  onClick={() => {
                    removeAccount({ variables: { id: account.id } });
                    setIsOpen(false);
                  }}
                  text="Remove"
                  icon="trash"
                  intent="danger"
                />
              )}
            </Menu>
          }
          canEscapeKeyClose={true}
          position={Position.BOTTOM_RIGHT}
          isOpen={isOpen && !updating}
          onClose={() => setIsOpen(false)}
        >
          <RoundedIconButton
            backgroundColor={Colors.LIGHT_GRAY5}
            loading={updating}
            icon="more"
            size={44}
            onClick={() => setIsOpen(!isOpen)}
          />
        </Popover>
      </Flex>
      <Flex>
        {hasPermissionError || hasAccountError ? (
          <Box py={2} px={3}>
            <Flex>
              <Box mr={2} mt={2} minWidth={20} height={20}>
                <Icon size={20} intent="danger" icon="warning-sign" />
              </Box>
              {hasPermissionError
                ? "Missing required permissions. "
                : "The account currently has issues. "}
              Please reconnect account and ensure you grant all requested
              permissions.
            </Flex>
            <Box mt={2}>
              {account.accountType === CalendarAccountAccountType.Google ? (
                !workspace ? (
                  <GrantCalendarAccess
                    responseGoogleHandler={responseGoogleHandler}
                    loading={loading}
                  />
                ) : (
                  <Flex>
                    <Box mr={2} mt={2} minWidth={20} height={20}>
                      <Icon size={20} intent="danger" icon="warning-sign" />
                    </Box>
                    Google account can only be added in personal mode
                  </Flex>
                )
              ) : (
                <GrantOutlookCalendarAccess />
              )}
            </Box>
          </Box>
        ) : null}
        {hasCalendarError ? (
          <Box py={2} px={3}>
            <Flex>
              <Box mr={2} mt={2} minWidth={20} height={20}>
                <Icon size={20} intent="danger" icon="warning-sign" />
              </Box>
              One of the calendar in your account currently has issues. Please
              reselect your calendar.
            </Flex>
            <Box mt={2}>
              <Button
                text="Calendar Setting"
                large={true}
                onClick={() => onOpenCalendarSettingModal(account)}
                fill={true}
                style={{ marginTop: "10px" }}
              />
            </Box>
          </Box>
        ) : null}
      </Flex>
    </Box>
  );
}
